import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { Spinner } from 'react-bootstrap'
import ShipmentCard from 'components/ShipmentCard'
import { colors } from 'globalStyles'
import { useSelector, useDispatch } from 'react-redux'
import { selectUserShipments, selectShipmentsLoading } from 'store/recipient/recipientSelectors'
import { getUserShipments } from 'store/recipient/recipientSlice'

const StyledMyShipments = styled.div`
  width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`

const StyledEmpty = styled.div`
  text-align: center;
  padding: 20px;
  color: ${colors.primary_black};
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-size: 16px;
  line-height: 1.5;

  p {
    margin: 0;
  }
`

const StyledList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 20px;
`

const StyledFilters = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
  padding: 8px 0;
  width: 100%;
  max-width: inherit;

  /* Make container responsive */
  @media (min-width: 768px) {
    flex-wrap: nowrap;
    justify-content: flex-start;
    overflow-x: visible;
  }

  /* For mobile, optimize for smaller screens */
  @media (max-width: 767px) {
    overflow-x: auto;
    -ms-overflow-style: none;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    padding-left: 0;
    padding-right: 0;
  }
`

const FilterButton = styled.button`
  padding: 0.4rem 1rem;
  border-radius: 22px;
  border: 2px solid transparent;
  font-size: 0.875rem;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: all 0.2s ease;
  min-width: fit-content;

  /* Desktop and tablet */
  @media (min-width: 768px) {
    padding: 0.5rem 1.25rem;
    flex: 0 1 auto;

    /* Natural sizing based on content */
    &[status='All'] {
      padding: 0.5rem 1rem;
      min-width: 60px;
    }
    &[status='Out For Delivery'] {
      padding: 0.5rem 1.5rem;
    }
  }

  /* Mobile */
  @media (max-width: 767px) {
    padding: 0.3rem 0.775rem;
    font-size: 0.9rem;
    &[status='All'] {
      min-width: 50px;
    }
    &[status='Out For Delivery'] {
      min-width: auto;
    }
  }

  /* Small mobile devices */
  @media (max-width: 390px) {
    padding: 0.3375rem 0.75rem;
    font-size: 0.7525rem;
  }

  /* Add black outline when active/selected */
  border: ${(props) =>
    props.isActive ? `2px solid ${colors.primary_black}` : '2px solid transparent'};

  background-color: ${(props) => {
    if (props.status === 'All') {
      return props.isActive ? colors.statusAllActive : colors.statusAllInactive
    }
    switch (props.status) {
      case 'Picked Up':
        return colors.statusPickedUp
      case 'Out For Delivery':
        return colors.statusOutForDelivery
      case 'Delivered':
        return colors.statusDelivered
      default:
        return colors.statusAllInactive
    }
  }};
  color: ${(props) => {
    if (props.status === 'All') {
      return colors.primary_white
    }
    return colors.primary_black
  }};

  &:hover {
    background-color: ${(props) => {
      if (props.status === 'All') {
        return props.isActive ? colors.statusAllHover : colors.statusHoverInactive
      }
      switch (props.status) {
        case 'Picked Up':
          return colors.statusPickedUp
        case 'Out For Delivery':
          return colors.statusOutForDelivery
        case 'Delivered':
          return colors.statusDelivered
        default:
          return colors.statusHoverInactive
      }
    }};
    color: ${(props) => (props.status === 'All' ? colors.primary_white : colors.primary_black)};
  }
`

const StyledPagination = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
  margin-top: 20px;

  button {
    padding: 8px 16px;
    background-color: ${colors.web_grey_light};
    color: ${colors.web_text_black};
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 16px;

    &:disabled {
      background-color: ${colors.web_lighter_grey};
      color: ${colors.secondary_light_grey};
      cursor: not-allowed;
    }
  }

  p {
    flex-basis: 100%;
    margin: 10px 0 0;
    font-size: 14px;
    color: ${colors.web_text_black};
    text-align: center;
  }
`

const StyledSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`

const SHIPMENT_STATUS = {
  ALL: 'All',
  PICKED_UP: 'Picked Up',
  OUT_FOR_DELIVERY: 'Out For Delivery',
  DELIVERED: 'Delivered',
}

const STATUS_BUTTON_CONTENT = {
  [SHIPMENT_STATUS.ALL]: 'shipment_card.all',
  [SHIPMENT_STATUS.PICKED_UP]: 'status_progress_bar.picked_up',
  [SHIPMENT_STATUS.OUT_FOR_DELIVERY]: 'status_progress_bar.out_for_delivery',
  [SHIPMENT_STATUS.DELIVERED]: 'status_progress_bar.delivered',
}

const ShipmentList = ({ shipments, onPrevious, onNext, hasPrevious, hasNext, t }) => (
  <>
    <StyledList>
      {shipments.map((shipment) => (
        <ShipmentCard
          key={`${shipment.shipmentid}-${shipment.packageid}`}
          shipment={shipment}
          packageNumber={shipment.packageNumber}
          totalPackages={shipment.totalPackages}
        />
      ))}
    </StyledList>

    <StyledPagination>
      {(hasPrevious || hasNext) && (
        <>
          <button type="button" onClick={onPrevious} disabled={!hasPrevious}>
            {t('shipment_card.previous')}
          </button>
          <button type="button" onClick={onNext} disabled={!hasNext}>
            {t('shipment_card.next')}
          </button>
        </>
      )}
      <p>{t('recipientInfo.display_note')}</p>
    </StyledPagination>
  </>
)

ShipmentList.propTypes = {
  shipments: PropTypes.arrayOf(
    PropTypes.shape({
      shipmentid: PropTypes.string.isRequired,
      shipmentpin: PropTypes.string.isRequired,
      deliverydate: PropTypes.string.isRequired,
      destinationiataid: PropTypes.string.isRequired,
      status: PropTypes.string.isRequired,
      vendor: PropTypes.string,
      packageNumber: PropTypes.number.isRequired,
      totalPackages: PropTypes.number.isRequired,
    })
  ).isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  hasPrevious: PropTypes.bool.isRequired,
  hasNext: PropTypes.bool.isRequired,
  t: PropTypes.func.isRequired,
}

const MyShipments = ({ userShipments }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const shipmentsState = useSelector(selectUserShipments)
  const loading = useSelector(selectShipmentsLoading)
  const [selectedStatus, setSelectedStatus] = useState(SHIPMENT_STATUS.ALL)
  const [packageOrderMap, setPackageOrderMap] = useState(new Map())
  const [hasAnyShipments, setHasAnyShipments] = useState(false)

  // Safely extract data and pagination with fallbacks
  const shipments = shipmentsState?.data || userShipments || []
  const pagination = shipmentsState?.pagination || { offset: 0, limit: 10, total: 0 }

  // Evaluate only while "All" is selected, so an empty result for another
  // filter never hides the filter bar
  useEffect(() => {
    if (!loading && selectedStatus === SHIPMENT_STATUS.ALL) {
      setHasAnyShipments(Array.isArray(shipments) && shipments.length > 0)
    }
  }, [shipments, loading, selectedStatus])

  // Update package order when shipments change
  useEffect(() => {
    if (Array.isArray(shipments)) {
      const newPackageOrderMap = new Map(packageOrderMap)

      shipments.forEach((shipment) => {
        if (!shipment.shipmentid || !shipment.packagepin) return

        const totalCount = shipment.shipmentpiecetotalcount || 1

        // Get or create the package stack for this shipment
        if (!newPackageOrderMap.has(shipment.shipmentid)) {
          // Initialize with empty slots for all packages
          const packages = Array(totalCount)
            .fill(null)
            .map((_, index) => ({
              packageid: null,
              packagepin: null,
              index,
            }))
          newPackageOrderMap.set(shipment.shipmentid, {
            packages,
            totalCount,
          })
        }

        const shipmentInfo = newPackageOrderMap.get(shipment.shipmentid)

        const existingIndex = shipmentInfo.packages.findIndex(
          (pkg) => pkg.packageid === shipment.packageid
        )
        if (existingIndex === -1) {
          const emptyIndex = shipmentInfo.packages.findIndex((pkg) => pkg.packageid === null)
          if (emptyIndex !== -1) {
            shipmentInfo.packages[emptyIndex] = {
              packageid: shipment.packageid,
              packagepin: shipment.packagepin,
              index: emptyIndex,
            }
          }
        }
      })

      setPackageOrderMap(newPackageOrderMap)
    }
  }, [shipments])

  const handleNext = () => {
    const nextOffset = pagination.offset + pagination.limit
    if (nextOffset < pagination.total) {
      dispatch(getUserShipments(nextOffset, selectedStatus))
    }
  }

  const handlePrevious = () => {
    const prevOffset = Math.max(0, pagination.offset - pagination.limit)
    if (pagination.offset > 0) {
      dispatch(getUserShipments(prevOffset, selectedStatus))
    }
  }

  const handleStatusChange = (status) => {
    setSelectedStatus(status)
    dispatch(getUserShipments(0, status))
  }

  const hasNextPage = pagination.offset + pagination.limit < pagination.total
  const hasPreviousPage = pagination.offset > 0

  const renderContent = () => {
    if (loading) {
      return (
        <StyledSpinner>
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t('recipientInfo.loading_shipments')}</span>
          </Spinner>
        </StyledSpinner>
      )
    }

    if (!Array.isArray(shipments) || shipments.length === 0) {
      return (
        <StyledEmpty>
          <p>{t('recipientInfo.no_shipments')}</p>
          <p>{t('recipientInfo.check_again')}</p>
          <p>{t('recipientInfo.display_note')}</p>
        </StyledEmpty>
      )
    }

    // Assign package numbers based on the order in the packageOrderMap
    const shipmentsWithPackageNumbers = shipments.map((shipment) => {
      const shipmentInfo = packageOrderMap.get(shipment.shipmentid) || {
        packages: [],
        totalCount: 1,
      }
      const packageIndex = shipmentInfo.packages.findIndex(
        (pkg) => pkg.packageid === shipment.packageid
      )
      const packageNumber = packageIndex + 1

      return {
        ...shipment,
        packageNumber,
        totalPackages: shipmentInfo.totalCount,
      }
    })

    return (
      <ShipmentList
        shipments={shipmentsWithPackageNumbers}
        onPrevious={handlePrevious}
        onNext={handleNext}
        hasPrevious={hasPreviousPage}
        hasNext={hasNextPage}
        t={t}
      />
    )
  }

  return (
    <StyledMyShipments>
      {hasAnyShipments && (
        <StyledFilters>
          {Object.values(SHIPMENT_STATUS).map((status) => (
            <FilterButton
              key={status}
              isActive={selectedStatus === status}
              status={status}
              onClick={() => handleStatusChange(status)}
            >
              {t(STATUS_BUTTON_CONTENT[status])}
            </FilterButton>
          ))}
        </StyledFilters>
      )}
      {renderContent()}
    </StyledMyShipments>
  )
}

MyShipments.propTypes = {
  userShipments: PropTypes.arrayOf(
    PropTypes.shape({
      haspickedup: PropTypes.bool,
      returnedtosender: PropTypes.bool,
      // Add other shipment properties as needed
    })
  ),
}

MyShipments.defaultProps = {
  userShipments: [],
}

export default MyShipments
